import { useEffect, useRef } from 'react'
import { useSnackbar } from 'notistack'
import { t } from '@lingui/macro'

import { useGetEmergencyRoomsQuery } from '@/core/services/room'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { isStaff } from '@/utils/type-guards'

import { useIsSA } from './useIsSA'

const EMERGENCY_SOUND_PATH = '/sound/emergency.wav'

/**
 * SEHA (en-SA) doctor-side alert for new emergency-room requests.
 *
 * The emergency-rooms query cache is kept live over socket.io
 * (`emergency_room_active` / `emergency_room_inactive`), so a room switching
 * to `standby` means a nurse started an emergency consultation and is waiting
 * for a physician. The first observation only records a baseline, so opening
 * the app on an already-waiting room does not re-alert.
 */
export function useEmergencyRoomAlerts() {
  const isSA = useIsSA()
  const authUser = useAppSelector(selectAuthUser)
  const { enqueueSnackbar } = useSnackbar()

  const isDoctor = isStaff(authUser) && authUser.status === 'MEDECIN'
  const enabled = isSA && isDoctor

  // Reuses the same RTK Query cache entry that App.tsx subscribes to.
  const { data: emergencyRooms } = useGetEmergencyRoomsQuery(undefined, { skip: !enabled })

  const knownStandbyRooms = useRef<Set<string> | null>(null)

  useEffect(() => {
    if (!enabled || !emergencyRooms) return

    const standbyRoomIds = new Set(emergencyRooms.filter((room) => room.status === 'standby').map((room) => room._id))

    if (knownStandbyRooms.current === null) {
      knownStandbyRooms.current = standbyRoomIds
      return
    }

    const newRequests = [...standbyRoomIds].filter((roomId) => !knownStandbyRooms.current?.has(roomId))
    knownStandbyRooms.current = standbyRoomIds
    if (!newRequests.length) return

    void new Audio(EMERGENCY_SOUND_PATH).play().catch(() => undefined)
    enqueueSnackbar(t`New emergency request — a patient is waiting for a physician`, {
      variant: 'error',
      autoHideDuration: 15000,
      preventDuplicate: true,
    })
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification(t`Emergency request`, {
        body: t`A patient is waiting for a physician in the emergency room`,
        icon: '/favicon.ico',
        tag: 'emergency-room-request',
        ...({ renotify: true } as NotificationOptions),
      })
      notification.onclick = () => window.focus()
    }
  }, [emergencyRooms, enabled, enqueueSnackbar])
}
